/**
 * What the profile should say about an account's email address.
 *
 * The Account card rendered its verification pill as
 * `{user.emailVerified && <StatusPill status="verified" />}` — present only
 * when verified. So the one state worth surfacing showed nothing at all: an
 * unverified member saw their address with no status beside it and no way to
 * act, while the Role line directly below has always rendered a pill either
 * way. Reported as "I do not see the status in the dashboard nor did I see a
 * method to trigger the verification email myself".
 *
 * The decision lives here rather than inline in the JSX so it can be tested —
 * the front-end suite covers the pure libraries, and components are verified by
 * lint and build. A conditional that renders nothing is exactly the kind of
 * thing that passes both of those while being wrong.
 */

/**
 * @param {object|null} user  The profile record (fresh read, or the cached
 *                            session copy when that read failed).
 * @returns {{ pill: 'verified'|'pending', canResend: boolean, email: string|null }}
 */
export function emailVerificationState(user) {
  // A missing or half-loaded record is treated as unverified: claiming
  // "verified" from absent data is the one wrong answer here, and the resend
  // action is harmless (the endpoint is idempotent and reveals nothing).
  const verified = Boolean(user && user.emailVerified === true);
  const email = (user && user.email) || null;

  return {
    pill: verified ? 'verified' : 'pending',
    // Nothing to resend to without an address, whatever the flag says.
    canResend: !verified && Boolean(email),
    email
  };
}
